import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { pluck, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { StoreFacadeService } from '../store-facade.service';
import { saveAs } from 'file-saver';

export interface RequestDocumento {
  result: ResultDocumento;
}

export interface ResultDocumento {
  iIdDocumento: number;
  dtCreacion: string;
  dtModificacion: string;
  iIdUsuarioCreacion: number;
  iIdUsuarioModificacion: number;
  vcNombre: string;
  vcRuta: string;
  vcGuid: string;
  siTipoDocumento: number;
  siEstado: number;
  iIdSubClaseDocumento: number;
}

@Injectable()
export class DocUploadService {
  private userId: number = 0;

  constructor(private http: HttpClient, private storeFacadeSv: StoreFacadeService,  @Inject('env') private env: any) {
    this.storeFacadeSv.selectProfile().subscribe(profile => {
      this.userId = profile.id as number;
    });
  }

  uploadFileToCloud(file: File, bucket: string, relativepath: string) {
    const url = `${this.env.api}/api/upload/uploadfile-to-cloud?bucket=${bucket}&relativepath=${relativepath}`;
    const formData = new FormData()
    formData.append('asset', file);
    return this.http.post(url, formData).pipe(pluck('result'));
  }

  getFileToCloud(id: any, isId: boolean = true) {
    const url = (isId) ? `${this.env.api}/v1/documento/obtenerfile-from-cloud-by-id/${id}` : `${this.env.api}/v1/documento/obtenerfile-from-cloud-by-guid/${id}`;
    return this.http.get<any>(url).pipe(pluck('result'));
  }

  downloadFileToCloudByPath(bucket: string, path: string) {
    const body = {
      bucket: bucket,
      ruta: path,
    }
    const url = `${this.env.api}/v1/documento/obtenerfile-from-cloud-by-path`;
    return this.http.post<any>(url, body).pipe(pluck('result')).pipe(
      tap((base64: string) => this.saveFile({ base64String: base64}))
    );
  }

  downloadFileToCloud(id: any, isId: boolean = true) {
    return this.getFileToCloud(id, isId).pipe(
      tap((base64: string) => this.saveFile({ base64String: base64}))
    );
  }

  saveFile({ base64String, sliceSize = 512 }: any) : {blob: any, name: string} {
    const [formatExtension, base64] = base64String.split('base64,');

    const byteCharacters = atob(base64);
    const byteArrays = [];
    const name = formatExtension.substring(5, formatExtension.indexOf(';'));

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays);

    saveAs(blob, name);

    return {
      blob: blob,
      name: name
    };
  }

  uploadFirmaToCloud(img_b64: string, bucket: string, relativepath: string) {
    let png = img_b64.split(',')[1];
    let binary = this.fixBinary(window.atob(png));
    let the_file = new Blob([binary], {type: 'image/png'});
    let imagen_firma = new File([the_file], 'imagen-firma.png', { type: 'image/png' });
    return this.uploadFileToCloud(imagen_firma, bucket, relativepath);
  }

  //////////////////////////////////////////////////////////

  uploadFile(file: File) {
    const [body, subClassDoc, userId, siTipoDoc] = this.getFileData(file);
    const url = `${this.env.api}/v1/documento/uploadfile/${subClassDoc}/${userId}/${siTipoDoc}`;
    return this.http.post(url, body).pipe(pluck('result'));
  }

  uploadFirma(img_b64: string) {
    let png = img_b64.split(',')[1];
    let binary = this.fixBinary(window.atob(png));// <-- Usamos la fn "fixBinary"
    let the_file = new Blob([binary], {type: 'image/png'});// <-- Sacamos el encode
    let imagen_firma = new File([the_file], 'imagen_firma.png', { type: 'image/png' });
    const [body, subClassDoc, userId, siTipoDoc] = this.getFileData(imagen_firma);
    const url = `${this.env.api}/v1/documento/uploadfile/${subClassDoc}/${userId}/${siTipoDoc}`;
    return this.http.post(url, body).pipe(pluck('result'));
  }

  uploadToCloud(file: File, directory: string) {
    const [body] = this.getFileData(file);
    const url = `${this.env.api}/v1/documento/uploadfile-to-cloud/${directory}`;
    return this.http.post(url, body).pipe(pluck('result'));
  }

  downloadFileFormat() {
    const id = '8f838f77-bf0b-44bc-ab51-3b5b92c4704c';
    const name = 'image';
    const format = 'png';

    return this.downloadFile(id, name, format);
  }

  downloadFile(id: string, name: string, format: string) {
    const url = `${this.env.api}/v1/documento/obtenerfile/${id}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: data, name }))
    );
  }

  downloadExcelTrabajadores(iIdEmpresa: number, iIdClient: number, iIdProvider: number) {
    let client = (iIdClient != null && iIdClient != undefined) ? iIdClient : '0';
    let provider = (iIdProvider != null && iIdProvider != undefined) ? iIdProvider : '0';
    const url = `${this.env.api}/v1/empresa/descargarexceltrabajadores/${iIdEmpresa}/${client}/${provider}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: data, name:'lista_trabajadores' }))
    );
  }

  downloadExcelPruebasFichas() {
    const url = `${this.env.api}/v1/catalogo-pruebas/descargarexcelpruebasfichas`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: `data:pruebas_fichas.xlsx;base64${data.split(";base64")[1]}`, name: `pruebas_fichas`}))
    );
  }

  downloadExcelParticulares(iIdCombo: number) {
    const url = `${this.env.api}/v1/request_quote_company/descargarexcelparaparticulares/${iIdCombo}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: data, name:'particulares' }))
    );
  }

  downloadExcelEntregables() {
    const url = `${this.env.api}/v1/catalogo-pruebas/descargarexcelentregables`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: `data:entregables.xlsx;base64${data.split(";base64")[1]}`, name: `entregables`}))
    );
  }

  getFile(guid: string) {
    const url = `${this.env.api}/v1/documento/obtenerfile/${guid}`;
    return this.http.get(url).pipe(pluck('result'));
  }

  downloadFileb64Data(id: string, name: string, format: string) {
    const url = `${this.env.api}/v1/documento/obtenerfile/${id}`;
    return this.http.get(url).pipe(pluck('result'));
  }

  private getFileData(file: File): [FormData, number, number, number] {
    const formData = new FormData();
    const iIdSubClaseDocumento = 2;
    const userId = this.userId;
    const siTipoDoc = 1;

    formData.append('asset', file);

    return [formData, iIdSubClaseDocumento, userId, siTipoDoc];
  }

  fixBinary (bin: any) {
    var length = bin.length;
    var buf = new ArrayBuffer(length);
    var arr = new Uint8Array(buf);
    for (var i = 0; i < length; i++) {
      arr[i] = bin.charCodeAt(i);
    }
    return buf;
  }

  downloadFileExcel(iIdCotiEmpresa: number, paraTitular: boolean) {
    const url = `${this.env.api}/v1/request_quote_company/descargarexcel/${iIdCotiEmpresa}/${paraTitular}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: `data:contrato_${iIdCotiEmpresa}_${paraTitular ? 'titular' : 'contratas'}.xlsx;base64${data.split(";base64")[1]}`, name: `contrato_${iIdCotiEmpresa}_${paraTitular ? 'titular' : 'contratas'}`}))
    );
  }

  downloadFileExcelParticulars(iIdCombo: number) {
    const url = `${this.env.api}/v1/request_quote_company/descargarexcelparaparticulares/${iIdCombo}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => this.saveFile({ base64String: data, name: 'precios_estandar' }))
    );
  }

  downloadFileExcelAll(iIdCotiEmpresa: number) {
    const url = `${this.env.api}/v1/request_quote_company/descargarexcel-all/${iIdCotiEmpresa}`;
    return this.http.get<any>(url).pipe(
      pluck('result'),
      tap((data: string) => {
        return this.saveFile({ base64String: `data:contrato_${iIdCotiEmpresa}.xlsx;base64${data.split(";base64")[1]}`, name: `contrato_${iIdCotiEmpresa}`})
      })
    );
  }

  getDocument(id: number): Observable<ResultDocumento> {
    return this.http
      .get<RequestDocumento>(`${this.env.api}/v1/documento/seleccionar/${id}`)
      .pipe(pluck('result'));
  }

  deleteDocument(id: number) {
    return this.http.delete(`${this.env.api}/v1/documento/eliminar/${id}`);
  }
}
